import { InfiniteData } from "@tanstack/react-query";

type NonFunctionPrimitive = boolean | number | string | bigint | undefined | object;

export interface UpsertInListArgs<T extends NonFunctionPrimitive> {
  list: T[];
  matchFn: (item: T, index: number) => boolean;
  updater: T | ((item?: T) => T | undefined);
  sortFn?: Parameters<Array<T>["sort"]>[0] | "prepend" | "append";
  updateAll?: boolean;
}

/**
 * Updates an item in a list if it matches the given condition, or inserts it if no match is found.
 *
 * @template T - The type of items in the list
 * @param args - Configuration object
 * @param args.list - The array to update
 * @param args.matchFn - Function to find the item to update
 * @param args.updater - New item or function that returns the updated item
 * @param [args.sortFn] - Optional sort function to apply after upserting
 * @param [args.updateAll=true] - Whether to update all matching items (true) or only the first (false)
 * @returns A new array with the item upserted
 *
 * @example
 * // Update existing item or insert new one
 * upsertInList({
 *   list: [{ id: 1, name: 'Alice' }, { id: 2, name: 'Bob' }],
 *   matchFn: (item) => item.id === 2,
 *   updater: { id: 2, name: 'Robert' }
 * });
 *
 * @example
 * // Update only first matching item
 * upsertInList({
 *   list: [1, 2, 2, 3],
 *   matchFn: (item) => item === 2,
 *   updater: 5,
 *   updateAll: false
 * });
 */
export const upsertInList = <T extends NonFunctionPrimitive>({
  list,
  matchFn,
  updater,
  sortFn,
  updateAll = true,
}: UpsertInListArgs<T>): T[] => {
  let updated = false;
  const updatedList = [...list].map((item, i) => {
    if ((!updateAll && !updated && matchFn(item, i)) || (updateAll && matchFn(item, i))) {
      updated = true;
      if (typeof updater === "function") {
        const newItem = updater(item);
        if (newItem === undefined) return item;
        return updater(item);
      }
      return updater;
    }
    return item;
  });

  if (!updated) {
    const newItem = typeof updater === "function" ? updater() : updater;
    if (newItem !== undefined) {
      return insertInList({
        list: updatedList,
        item: newItem,
        sortFn,
      });
    }
  }

  if (sortFn && typeof sortFn !== "string") {
    updatedList.sort(sortFn);
  }

  return updatedList;
};

export interface InsertInListArgs<T extends NonFunctionPrimitive> {
  list: T[];
  item: T;
  sortFn?: Parameters<Array<T>["sort"]>[0] | "append" | "prepend";
}

/**
 * Inserts an item into a list with optional positioning or sorting.
 *
 * @template T - The type of items in the list
 * @param args - Configuration object
 * @param args.list - The array to insert into
 * @param args.item - The item to insert
 * @param [args.sortFn] - How to position the item: "prepend", "append", or a sort function
 * @returns A new array with the item inserted
 *
 * @example
 * // Prepend item
 * insertInList({ list: [2, 3], item: 1, sortFn: "prepend" }); // [1, 2, 3]
 *
 * @example
 * // Append item
 * insertInList({ list: [1, 2], item: 3, sortFn: "append" }); // [1, 2, 3]
 *
 * @example
 * // Insert with sorting
 * insertInList({ list: [1, 3], item: 2, sortFn: (a, b) => a - b }); // [1, 2, 3]
 */
export const insertInList = <T extends NonFunctionPrimitive>({ list, item, sortFn }: InsertInListArgs<T>) => {
  if (sortFn === "prepend") {
    return prependToList({
      list,
      item,
    });
  } else if (sortFn === "append") {
    return appendToList({
      list,
      item,
    });
  }
  const updatedList = [...list, item];

  if (sortFn) {
    updatedList.sort(sortFn);
  }

  return updatedList;
};

export interface AppendToListArgs<T extends NonFunctionPrimitive> {
  list: T[];
  item: T;
}

/**
 * Appends an item to the end of a list.
 *
 * @template T - The type of items in the list
 * @param args - Configuration object
 * @param args.list - The array to append to
 * @param args.item - The item to append
 * @returns A new array with the item appended
 *
 * @example
 * appendToList({ list: [1, 2], item: 3 }); // [1, 2, 3]
 */
export const appendToList = <T extends NonFunctionPrimitive>({ list, item }: AppendToListArgs<T>) => {
  return [...list, item];
};

export interface PrependToListArgs<T extends NonFunctionPrimitive> {
  list: T[];
  item: T;
}

/**
 * Prepends an item to the beginning of a list.
 *
 * @template T - The type of items in the list
 * @param args - Configuration object
 * @param args.list - The array to prepend to
 * @param args.item - The item to prepend
 * @returns A new array with the item prepended
 *
 * @example
 * prependToList({ list: [2, 3], item: 1 }); // [1, 2, 3]
 */
export const prependToList = <T extends NonFunctionPrimitive>({ list, item }: AppendToListArgs<T>) => {
  return [item, ...list];
};

export interface RemoveFromListArgs<T extends NonFunctionPrimitive> {
  list: T[];
  matchFn: (item: T, index: number) => boolean;
}

/**
 * Removes items from a list that match the given condition.
 *
 * @template T - The type of items in the list
 * @param args - Configuration object
 * @param args.list - The array to remove items from
 * @param args.matchFn - Function that returns true for items to remove
 * @returns A new array with matching items removed
 *
 * @example
 * removeFromList({
 *   list: [1, 2, 3, 4],
 *   matchFn: (item) => item % 2 === 0 // Remove even numbers
 * }); // [1, 3]
 */
export const removeFromList = <T extends NonFunctionPrimitive>({ list, matchFn }: RemoveFromListArgs<T>) => {
  return list.filter((item, index) => !matchFn(item, index));
};

export interface UpdateInListArgs<T extends NonFunctionPrimitive> {
  list: T[];
  matchFn: (item: T, index: number) => boolean;
  updater: T | ((item: T) => T);
  sortFn?: Parameters<Array<T>["sort"]>[0];
  updateAll?: boolean;
}

/**
 * Updates items in a list that match the given condition. Does not insert if no match is found.
 *
 * @template T - The type of items in the list
 * @param args - Configuration object
 * @param args.list - The array to update
 * @param args.matchFn - Function to find items to update
 * @param args.updater - New item or function that returns the updated item
 * @param [args.sortFn] - Optional sort function to apply after updating
 * @param [args.updateAll=true] - Whether to update all matching items (true) or only the first (false)
 * @returns A new array with matching items updated
 *
 * @example
 * updateInList({
 *   list: [{ id: 1, name: 'Alice' }, { id: 2, name: 'Bob' }],
 *   matchFn: (item) => item.id === 2,
 *   updater: (item) => ({ ...item, name: 'Robert' })
 * });
 */
export const updateInList = <T extends NonFunctionPrimitive>({
  list,
  matchFn,
  updater,
  sortFn,
  updateAll = true,
}: UpdateInListArgs<T>): T[] => {
  let updated = false;
  const updatedList = [...list].map((item, i) => {
    if ((!updateAll && !updated && matchFn(item, i)) || (updateAll && matchFn(item, i))) {
      updated = true;
      if (typeof updater === "function") {
        const newItem = updater(item);
        if (newItem === undefined) return item;
        return updater(item);
      }
      return updater;
    }
    return item;
  });

  if (sortFn) {
    updatedList.sort(sortFn);
  }

  return updatedList;
};

type PrependToInfiniteDataListArgs<T> = {
  infiniteData: InfiniteData<T[]>;
  item: T;
  updatePageParams?: (oldPageParams: InfiniteData<T[]>["pageParams"]) => InfiniteData<T[]>["pageParams"];
};

/**
 * Prepends an item to the first page of an InfiniteData structure.
 *
 * @template TPage - The type of each page in the infinite data
 * @template TItem - The type of items in the list
 * @param args - Configuration object
 * @param args.infiniteData - The infinite query data structure
 * @param args.item - The item to prepend
 * @param [args.updatePageParams] - Optional function to update page params
 * @returns Updated infinite data with the item prepended to the first page
 *
 * @example
 * prependToInfiniteDataList({
 *   infiniteData: { pages: [[1, 2], [3, 4]], pageParams: [0, 2] },
 *   item: 0
 * });
 */

export const prependToInfiniteDataList = <T extends NonFunctionPrimitive>({
  infiniteData,
  item,
  updatePageParams,
}: PrependToInfiniteDataListArgs<T>): InfiniteData<T[]> => {
  const updatedPages = upsertInList({
    list: infiniteData.pages,
    matchFn: (_, index) => index === 0,
    updater: (page) => {
      // If page is undefined (no first page exists), create a new page with just the item
      if (!page) return [item];
      const updatedList = prependToList({
        list: page,
        item,
      });
      return updatedList;
    },
  });

  // If we created a new page (pages array went from 0 to 1 length), add a pageParam
  const needsNewPageParam = infiniteData.pages.length === 0 && updatedPages.length === 1;
  const updatedPageParams = needsNewPageParam
    ? [0] // Default first page param
    : updatePageParams?.(infiniteData.pageParams) ?? infiniteData.pageParams;

  return {
    pages: updatedPages,
    pageParams: updatedPageParams,
  };
};

type AppendToInfiniteDataListArgs<T extends NonFunctionPrimitive> = PrependToInfiniteDataListArgs<T>;

/**
 * Appends an item to the last page of an InfiniteData structure.
 *
 * @template TPage - The type of each page in the infinite data
 * @template TItem - The type of items in the list
 * @param args - Configuration object
 * @param args.infiniteData - The infinite query data structure
 * @param args.item - The item to append
 * @param [args.updatePageParams] - Optional function to update page params
 * @returns Updated infinite data with the item appended to the last page
 *
 * @example
 * appendToInfiniteDataList({
 *   infiniteData: { pages: [[1, 2], [3, 4]], pageParams: [0, 2] },
 *   item: 5
 * });
 */
export const appendToInfiniteDataList = <T extends NonFunctionPrimitive>({
  infiniteData,
  item,
  updatePageParams,
}: AppendToInfiniteDataListArgs<T>): InfiniteData<T[]> => {
  return {
    pages: upsertInList({
      list: infiniteData.pages,
      matchFn: (_, index) => index === infiniteData.pages.length - 1,
      updater: (page) => {
        if (!page) return page;
        const updatedList = appendToList({
          list: page,
          item,
        });
        return updatedList;
      },
    }),
    pageParams: updatePageParams?.(infiniteData.pageParams) ?? infiniteData.pageParams,
  };
};

type UpsertInInfiniteDataListArgs<T extends NonFunctionPrimitive, TPageParam> = {
  infiniteData: InfiniteData<T[], TPageParam> | undefined;
  updater: T | ((item?: T) => T | undefined);
  matchFn: (item: T, index: number) => boolean;
  sortFn?: Parameters<Array<T>["sort"]>[0] | "append" | "prepend";
  updatePageParams?: (lastPage: T[], allPages: T[][]) => TPageParam;
  updateAll?: boolean;
};

/**
 * Updates an item in an InfiniteData structure if it matches the condition, or inserts it if not found.
 * Searches across all pages for the matching item.
 *
 * @template TPage - The type of each page in the infinite data
 * @template TItem - The type of items in the list
 * @param args - Configuration object
 * @param args.infiniteData - The infinite query data structure
 * @param args.item - The item to upsert
 * @param args.matchFn - Function to find the item to update
 * @param [args.sortFn] - How to position the item if inserted: "prepend", "append" (default), or a sort function
 * @param [args.updatePageParams] - Optional function to update page params
 * @param [args.updateAll=true] - Whether to update all matching items (true) or only the first (false)
 * @returns Updated infinite data with the item upserted
 *
 * @example
 * upsertInInfiniteDataList({
 *   infiniteData: { pages: [[{ id: 1 }, { id: 2 }]], pageParams: [0] },
 *   item: { id: 2, name: 'Updated' },
 *   matchFn: (user) => user.id === 2,
 *   sortFn: "append"
 * });
 */
export const upsertInInfiniteDataList = <T extends NonFunctionPrimitive, TPageParam>({
  infiniteData,
  updater,
  matchFn,
  sortFn,
  updatePageParams,
  updateAll = true,
}: UpsertInInfiniteDataListArgs<T, TPageParam>): InfiniteData<T[], TPageParam | undefined> | undefined => {
  // 1. Flatted infiniteData to allow for better sorting according to `sortFn`.
  if (!infiniteData) return undefined;

  const flattenedList = infiniteData.pages.flat();
  const originalPageLengths = infiniteData.pages.length ? infiniteData.pages.map((page) => page.length) : [0];

  // 2. Upsert matching items based on `matchFn`, `updater`, and `updateAll` options
  let updated = false;
  let updatedList = flattenedList.map((item, i) => {
    if ((!updateAll && !updated && matchFn(item, i)) || (updateAll && matchFn(item, i))) {
      updated = true;
      if (typeof updater === "function") {
        const newItem = updater(item);
        if (newItem === undefined) return item;
        return newItem;
      }
      return updater;
    }
    return item;
  });

  // If no match found, insert the new item
  if (!updated) {
    const newItem = typeof updater === "function" ? updater() : updater;
    if (newItem !== undefined) {
      // 3. Sort flattened array (after upsert) according to `sortFn`
      //   a. If `sortFn` is "prepend" or "append", those only apply to inserted items, not updated items.
      if (sortFn === "prepend") {
        updatedList = [newItem, ...updatedList];
        const first = originalPageLengths[0];
        if (first !== undefined) originalPageLengths[0] = first + 1;
      } else if (sortFn === "append" || !sortFn) {
        updatedList = [...updatedList, newItem];
        const lastIdx = originalPageLengths.length - 1;
        const last = originalPageLengths[lastIdx];
        if (last !== undefined) originalPageLengths[lastIdx] = last + 1;
      } else {
        // Custom sort function - add item and then sort
        updatedList = [...updatedList, newItem];
        updatedList.sort(sortFn);
        const firstIdx0 = originalPageLengths[0];
        if (updatedList[0] === newItem) {
          if (firstIdx0 !== undefined) originalPageLengths[0] = firstIdx0 + 1;
        } else if (updatedList.at(-1) === newItem) {
          const lastIdx = originalPageLengths.length - 1;
          const last = originalPageLengths[lastIdx];
          if (last !== undefined) originalPageLengths[lastIdx] = last + 1;
        }
      }
    }
  } else if (sortFn && sortFn !== "prepend" && sortFn !== "append") {
    // If we updated an existing item and have a custom sort function, apply it
    updatedList.sort(sortFn);
  }

  // 4. Split updated, flattened array into pages of the same length as each original page.
  const updatedPages: T[][] = [];
  let currentIndex = 0;

  for (const pageLength of originalPageLengths) {
    const page = updatedList.slice(currentIndex, currentIndex + pageLength);
    if (page.length >= 0) {
      updatedPages.push(page);
    }
    currentIndex += pageLength;
  }

  // Handle remaining items if the list grew (e.g., from insertion)
  if (currentIndex < updatedList.length) {
    const remainingItems = updatedList.slice(currentIndex);
    const lastPage = updatedPages[updatedPages.length - 1];
    if (lastPage !== undefined) {
      // Append to the last page
      updatedPages[updatedPages.length - 1] = [...lastPage, ...remainingItems];
    } else {
      // No pages exist, create a new one
      updatedPages.push(remainingItems);
    }
  }

  // 5. Update pageParams array by running `updatePageParams` on each page to generate a matching array of page params based on updated pages. If no `updatePageParams` provided, just use original pageParams with no changes
  let updatedPageParams: (TPageParam | undefined)[];

  if (updatePageParams) {
    updatedPageParams = updatedPages.map((page, index) => {
      if (index < infiniteData.pages.length) {
        // Use existing pageParam as base, let updatePageParams derive from the page data
        return updatePageParams(page, updatedPages.slice(0, index + 1));
      }
      // For new pages, still call updatePageParams to derive the param
      return updatePageParams(page, updatedPages.slice(0, index + 1));
    });
  } else {
    // If no updatePageParams provided, use original pageParams
    // Add a default param for any new pages that were created
    updatedPageParams = [...infiniteData.pageParams];
    while (updatedPageParams.length < updatedPages.length) {
      const lastParam = updatedPageParams[updatedPageParams.length - 1];
      updatedPageParams.push(lastParam);
    }
  }

  // 6. Return result.
  return {
    pages: updatedPages,
    pageParams: updatedPageParams,
  };
};

type RemoveItemFromInfiniteDataListArgs<T extends NonFunctionPrimitive, TPageParam> = {
  infiniteData: InfiniteData<T[], TPageParam> | undefined;
  matchFn: (item: T, index: number) => boolean;
  updatePageParams?: (lastPage: T[], allPages: T[][]) => TPageParam;
};

/**
 * Removes items from an InfiniteData structure that match the given condition.
 * Searches across all pages and removes all matching items.
 *
 * @template TPage - The type of each page in the infinite data
 * @template TItem - The type of items in the list
 * @param args - Configuration object
 * @param args.infiniteData - The infinite query data structure
 * @param args.matchFn - Function that returns true for items to remove
 * @param [args.updatePageParams] - Optional function to update page params
 * @returns Updated infinite data with matching items removed
 *
 * @example
 * removeItemFromInfiniteDataList({
 *   infiniteData: { pages: [[{ id: 1 }, { id: 2 }]], pageParams: [0] },
 *   matchFn: (user) => user.id === 2
 * });
 */
export const removeItemFromInfiniteDataList = <T extends NonFunctionPrimitive, TPageParam>({
  infiniteData,
  matchFn,
  updatePageParams,
}: RemoveItemFromInfiniteDataListArgs<T, TPageParam>): InfiniteData<T[], TPageParam> | undefined => {
  if (!infiniteData) return undefined;
  const updatedPages = infiniteData.pages.map((page) => {
    const updatedList = page.filter((listItem, index) => !matchFn(listItem, index));

    // Only update the page if items were actually removed
    if (updatedList.length !== page.length) {
      return updatedList;
    }

    return page;
  });

  return {
    pages: updatedPages,
    pageParams: updatePageParams
      ? updatedPages.map((page, i) => updatePageParams(page, updatedPages.slice(0, i + 1)))
      : infiniteData.pageParams,
  };
};

/**
 * Type representing an operation that can be performed on infinite data lists.
 * These match the function signatures of the infinite data list utilities above.
 */
type InfiniteDataListOperation<T extends NonFunctionPrimitive, TPageParam = unknown> =
  | {
      type: "upsert";
      args: Omit<UpsertInInfiniteDataListArgs<T, TPageParam>, "infiniteData">;
    }
  | {
      type: "remove";
      args: Omit<RemoveItemFromInfiniteDataListArgs<T, TPageParam>, "infiniteData">;
    }
  | {
      type: "prepend";
      args: Omit<PrependToInfiniteDataListArgs<T>, "infiniteData">;
    }
  | {
      type: "append";
      args: Omit<AppendToInfiniteDataListArgs<T>, "infiniteData">;
    };

interface WithNestedInfiniteDataListArgs<
  TPage extends Record<string, NonFunctionPrimitive>,
  ItemKey extends keyof TPage,
  TItem extends NonFunctionPrimitive = TPage[ItemKey] extends Array<NonFunctionPrimitive>
    ? TPage[ItemKey][number]
    : NonFunctionPrimitive,
> {
  infiniteData: InfiniteData<TPage> | undefined;
  listKey: ItemKey;
  operation: InfiniteDataListOperation<TItem>;
  updatePageParams?: (oldPageParams: InfiniteData<TPage>["pageParams"]) => InfiniteData<TPage>["pageParams"];
}

/**
 * Applies list manipulation operations to InfiniteData structures where pages are objects
 * containing arrays, rather than being arrays themselves.
 *
 * This utility unwraps nested page objects, applies the specified operation using existing
 * infinite data list utilities, and wraps the results back into the original structure.
 *
 * @template TPage - The type of each page object in the infinite data
 * @template TItem - The type of items in the nested list
 * @param args - Configuration object
 * @param args.infiniteData - The infinite query data structure with nested pages
 * @param args.listKey - The key in the page object that contains the array
 * @param args.operation - The operation to perform (upsert, remove, prepend, or append)
 * @param [args.updatePageParams] - Optional function to update page params
 * @returns Updated infinite data with the operation applied to the nested lists
 *
 * @example
 * // For InfiniteData<{ items: User[], total: number }>
 * const result = withNestedInfiniteDataList({
 *   infiniteData: {
 *     pages: [
 *       { items: [{ id: 1 }, { id: 2 }], total: 10 },
 *       { items: [{ id: 3 }, { id: 4 }], total: 10 }
 *     ],
 *     pageParams: [0, 2]
 *   },
 *   listKey: 'items',
 *   operation: {
 *     type: 'upsert',
 *     args: {
 *       updater: { id: 2, name: 'Updated' },
 *       matchFn: (user) => user.id === 2,
 *       sortFn: 'append'
 *     }
 *   }
 * });
 *
 * @example
 * // For InfiniteData<{ data: Message[], hasMore: boolean }>
 * const result = withNestedInfiniteDataList({
 *   infiniteData: queryData,
 *   listKey: 'data',
 *   operation: {
 *     type: 'prepend',
 *     args: {
 *       item: newMessage
 *     }
 *   }
 * });
 *
 * @example
 * // Removing an item
 * const result = withNestedInfiniteDataList({
 *   infiniteData: queryData,
 *   listKey: 'results',
 *   operation: {
 *     type: 'remove',
 *     args: {
 *       matchFn: (item) => item.id === deletedId
 *     }
 *   }
 * });
 */
export const withNestedInfiniteDataList = <
  TPage extends Record<string, NonFunctionPrimitive>,
  ItemKey extends keyof TPage,
  TItem extends NonFunctionPrimitive = TPage[ItemKey] extends Array<NonFunctionPrimitive>
    ? TPage[ItemKey][number]
    : NonFunctionPrimitive,
>({
  infiniteData,
  listKey,
  operation,
  updatePageParams,
}: WithNestedInfiniteDataListArgs<TPage, ItemKey, TItem>): InfiniteData<TPage> | undefined => {
  if (!infiniteData) return undefined;

  // Extract the arrays from the nested page objects
  const unwrappedData: InfiniteData<TItem[]> = {
    pages: infiniteData.pages.map((page) => page[listKey] as TItem[]),
    pageParams: infiniteData.pageParams,
  };

  // Apply the operation using existing utilities
  let updatedUnwrappedData: InfiniteData<TItem[]> | undefined;

  switch (operation.type) {
    case "upsert":
      updatedUnwrappedData = upsertInInfiniteDataList({
        infiniteData: unwrappedData,
        ...operation.args,
      });
      break;
    case "remove":
      updatedUnwrappedData = removeItemFromInfiniteDataList({
        infiniteData: unwrappedData,
        ...operation.args,
      });
      break;
    case "prepend":
      updatedUnwrappedData = prependToInfiniteDataList({
        infiniteData: unwrappedData,
        ...operation.args,
      });
      break;
    case "append":
      updatedUnwrappedData = appendToInfiniteDataList({
        infiniteData: unwrappedData,
        ...operation.args,
      });
      break;
  }

  if (!updatedUnwrappedData) return undefined;

  // Wrap the updated arrays back into the page structure
  const wrappedPages = updatedUnwrappedData.pages.map((updatedList, index) => {
    const originalPage = infiniteData.pages[index];
    if (!originalPage) {
      // If this is a new page (e.g., from prepend/append), create a minimal page object
      return {
        [listKey]: updatedList,
      } as TPage;
    }
    return {
      ...originalPage,
      [listKey]: updatedList,
    };
  });

  return {
    pages: wrappedPages,
    pageParams: updatePageParams?.(updatedUnwrappedData.pageParams) ?? updatedUnwrappedData.pageParams,
  };
};
